import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { RefundManagementService, RefundRequest } from '../services/refund-management.service';
import { StoreService } from '../services/store.service';
import { PhotoboothService } from '../services/photobooth.service';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { deviceBelongsToBrand } from '../services/device-brand.util';
import { findPhotoLavieBrand } from '../services/coupon-brand.util';
import { environment } from '../../environments/environment';
import { formatPacificDate, formatPacificDateTime, isDateOnlyString, toPacificDateString, todayInPacific } from '../utils/pacific-time.util';

@Component({
  selector: 'app-refund-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './refund-management.component.html',
  styleUrls: ['./refund-management.component.scss']
})
export class RefundManagementComponent implements OnInit, OnDestroy {
  private refundService = inject(RefundManagementService);
  private storeService = inject(StoreService);
  private photoboothService = inject(PhotoboothService);
  private brandService = inject(BrandService);
  private auth = inject(Auth);

  refundRequests$: Observable<RefundRequest[]> = this.refundService.getRefundRequests();
  stores$: Observable<any[]> = this.storeService.getStores();
  brands$: Observable<Brand[]> = this.brandService.getBrands();
  devices$: Observable<any[]> = this.storeService.getAllDevicesLive();
  allRequests: RefundRequest[] = [];
  filteredRequests: RefundRequest[] = [];
  allBrands: Brand[] = [];
  allDevices: any[] = [];
  allStores: any[] = [];
  private brandsSub?: Subscription;
  private devicesSub?: Subscription;
  private storesSub?: Subscription;

  // Filter States
  selectedStatusFilter: string = 'needs_review'; // Default to needs_review for staff focus
  searchQuery: string = '';
  selectedDateFilter: string = '';

  // Pagination State
  currentPage: number = 1;
  pageSize: number = 25;
  pageSizeOptions: number[] = [10, 25, 50, 100];

  // Evidence / Attached Files State (KAN-24)
  evidenceManifests: any[] = [];
  evidenceUrls: { [attachmentId: string]: string } = {};
  isLoadingEvidence: boolean = false;
  activeEvidenceLightboxUrl: string | null = null;
  activeEvidenceLightboxTitle: string | null = null;
  private evidenceSub?: Subscription;


  get totalPages(): number {
    return Math.ceil(this.filteredRequests.length / this.pageSize) || 1;
  }

  get paginatedRequests(): RefundRequest[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredRequests.slice(startIndex, startIndex + this.pageSize);
  }

  get paginationRange(): string {
    if (this.filteredRequests.length === 0) return '0 of 0';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.filteredRequests.length);
    return `${start}–${end} of ${this.filteredRequests.length}`;
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Modal / Detail States
  selectedRequest: RefundRequest | null = null;
  isDetailModalOpen: boolean = false;

  // Session details slide-out (inside the review modal)
  isSessionPanelOpen: boolean = false;
  isSessionLoading: boolean = false;
  sessionDetail: any = null;
  sessionPanelError: string | null = null;
  isApproveModalOpen: boolean = false;
  isDenyModalOpen: boolean = false;
  isRequestInfoModalOpen: boolean = false;
  isManualCreateModalOpen: boolean = false;
  isSettingsModalOpen: boolean = false;

  // Settings Form State
  autoValidationEnabled: boolean = true;
  deepLogInvestigationEnabled: boolean = false;
  deepLogAutoFetchKiosk: boolean = true;
  investigationTimeoutSec: number = 20;
  customSemanticQuestions: string[] = [];
  newSemanticQuestion: string = '';
  isUpdatingSetting: boolean = false;

  // Manual Refund Form Fields
  manualEmail: string = '';
  manualBrandId: string = '';
  manualBrandName: string = '';
  manualStore: string = '';
  manualDeviceId: string = '';
  manualSessionId: string = '';
  manualSessionDate: string = '';
  manualApproxTime: string = '';
  manualPaymentMethod: string = 'Credit Card';
  manualAmount: number = 10.0;
  manualReasonCategory: string = 'Booth charged but did not start';
  manualReasonText: string = '';
  manualDecisionMode: 'instant_approve' | 'review_queue' = 'instant_approve';
  manualCouponTitle: string = 'Manual Refund Voucher';
  manualDiscountType: 'free_session' | 'fixed' | 'percent' = 'free_session';
  manualDiscountValue: number = 10.0;
  manualWantsCash: boolean = false;
  manualNotes: string = '';

  defaultManualBrand(): void {
    const storageBrandId = this.brandService.getSelectedBrandIdFromStorage();
    const storageBrand = storageBrandId ? this.allBrands.find(b => b.id === storageBrandId) : null;
    if (storageBrand) {
      this.manualBrandId = storageBrand.id || '';
      this.manualBrandName = storageBrand.name || '';
    } else {
      const defaultBrand = findPhotoLavieBrand(this.allBrands);
      if (defaultBrand) {
        this.manualBrandId = defaultBrand.brandId;
        this.manualBrandName = defaultBrand.brandName;
      } else if (this.allBrands.length > 0) {
        this.manualBrandId = this.allBrands[0].id || '';
        this.manualBrandName = this.allBrands[0].name || '';
      } else {
        this.manualBrandId = '';
        this.manualBrandName = '';
      }
    }
  }

  getFilteredManualDevices(): any[] {
    if (!this.allDevices || this.allDevices.length === 0) return [];
    return this.allDevices
      .filter(dev => {
        if (!dev || dev.isDeleted || dev.id === 'global') return false;
        return deviceBelongsToBrand(dev, this.manualBrandId, this.manualBrandName);
      })
      .sort((a, b) => {
        const idA = String(a.deviceId || a.id || '').toUpperCase();
        const idB = String(b.deviceId || b.id || '').toUpperCase();
        return idA.localeCompare(idB);
      });
  }

  onManualBrandChange(): void {
    const found = this.allBrands.find(b => b.id === this.manualBrandId);
    this.manualBrandName = found ? found.name : '';

    if (this.manualDeviceId) {
      const filtered = this.getFilteredManualDevices();
      const isStillValid = filtered.some(d => (d.deviceId || d.id) === this.manualDeviceId);
      if (!isStillValid) {
        this.manualDeviceId = '';
      }
    }
  }

  onManualDeviceChange(): void {
    if (!this.manualDeviceId) return;
    const dev = this.allDevices.find(d => (d.deviceId || d.id) === this.manualDeviceId);
    if (dev) {
      const storeIdOrDocId = dev.storeDocId || dev.storeId;
      const matchedStore = this.allStores.find(s => s.id === storeIdOrDocId || s.storeId === storeIdOrDocId || s.name === storeIdOrDocId);
      if (matchedStore) {
        this.manualStore = matchedStore.name || matchedStore.friendlyName || matchedStore.id;
      } else if (dev.storeName || dev.storeLocation) {
        this.manualStore = dev.storeName || dev.storeLocation;
      }
    }
  }

  onManualCashToggle(): void {
    if (this.manualWantsCash) {
      this.manualDecisionMode = 'review_queue';
    }
  }

  // Decision Form Fields
  approveCouponTitle: string = '';
  approveDiscountType: 'free_session' | 'fixed' | 'percent' = 'free_session';
  approveDiscountValue: number = 10.0;
  approveNotes: string = '';
  denyRationale: string = '';
  requestInfoMessage: string = '';

  // Loading / Feedback States
  isProcessingAction: boolean = false;
  actionSuccessMessage: string | null = null;
  actionErrorMessage: string | null = null;

  get staffEmail(): string {
    return this.auth.currentUser?.email || localStorage.getItem('userEmail') || 'staff@kceg.global';
  }

  ngOnInit(): void {
    this.refundRequests$.subscribe(requests => {
      this.allRequests = requests || [];
      this.applyFilters();
    });

    this.brandsSub = this.brands$.subscribe(brands => {
      this.allBrands = brands || [];
      if (this.isManualCreateModalOpen && !this.manualBrandId && this.allBrands.length > 0) {
        this.defaultManualBrand();
      }
    });

    this.devicesSub = this.devices$.subscribe(devices => {
      this.allDevices = devices || [];
    });

    this.storesSub = this.stores$.subscribe(stores => {
      this.allStores = stores || [];
    });

    this.refundService.getRefundSettings().subscribe({
      next: (settings) => {
        this.autoValidationEnabled = settings.autoValidationEnabled !== false;
        this.deepLogInvestigationEnabled = settings.deepLogInvestigationEnabled === true;
        this.deepLogAutoFetchKiosk = settings.deepLogAutoFetchKiosk !== false;
        this.investigationTimeoutSec = settings.investigationTimeoutSec || 20;
        this.customSemanticQuestions = Array.isArray(settings.customSemanticQuestions) ? [...settings.customSemanticQuestions] : [];
      },
      error: (err) => console.warn('Could not load refund settings:', err)
    });
  }

  openSettingsModal(): void {
    this.isSettingsModalOpen = true;
  }

  closeSettingsModal(): void {
    this.isSettingsModalOpen = false;
    this.newSemanticQuestion = '';
  }

  async saveSettings(): Promise<void> {
    this.isUpdatingSetting = true;
    try {
      await this.refundService.updateRefundSettings({
        autoRefundValidationEnabled: this.autoValidationEnabled,
        deepLogInvestigationEnabled: this.deepLogInvestigationEnabled,
        deepLogAutoFetchKiosk: this.deepLogAutoFetchKiosk,
        investigationTimeoutSec: Number(this.investigationTimeoutSec) || 20,
        customSemanticQuestions: this.customSemanticQuestions
      });
      this.actionSuccessMessage = 'Refund settings successfully updated!';
      this.closeSettingsModal();
      setTimeout(() => this.actionSuccessMessage = null, 4000);
    } catch (err: any) {
      console.error('Error updating refund settings:', err);
      this.actionErrorMessage = 'Failed to update settings: ' + (err.message || err);
      setTimeout(() => this.actionErrorMessage = null, 5000);
    } finally {
      this.isUpdatingSetting = false;
    }
  }

  addCustomSemanticQuestion(): void {
    const q = this.newSemanticQuestion.trim();
    if (!q) return;
    if (!this.customSemanticQuestions.includes(q)) {
      this.customSemanticQuestions.push(q);
    }
    this.newSemanticQuestion = '';
  }

  removeCustomSemanticQuestion(index: number): void {
    if (index >= 0 && index < this.customSemanticQuestions.length) {
      this.customSemanticQuestions.splice(index, 1);
    }
  }

  async toggleAutoValidation(): Promise<void> {
    this.isUpdatingSetting = true;
    const newState = !this.autoValidationEnabled;
    try {
      await this.refundService.updateRefundSettings({
        autoRefundValidationEnabled: newState
      });
      this.autoValidationEnabled = newState;
      this.actionSuccessMessage = `Automated validation is now ${newState ? 'ENABLED' : 'DISABLED'}.`;
      setTimeout(() => this.actionSuccessMessage = null, 3500);
    } catch (err: any) {
      console.error('Error toggling auto-validation setting:', err);
      this.actionErrorMessage = 'Failed to update auto-validation setting: ' + (err.message || err);
      setTimeout(() => this.actionErrorMessage = null, 4000);
    } finally {
      this.isUpdatingSetting = false;
    }
  }

  // --- Filter Logic ---

  setStatusFilter(status: string): void {
    this.selectedStatusFilter = status;
    this.currentPage = 1;
    this.applyFilters();
  }

  onSearchChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  onDateFilterChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    let result = [...this.allRequests];

    // Status Filter
    if (this.selectedStatusFilter !== 'all') {
      result = result.filter(r => r.status === this.selectedStatusFilter);
    }

    // Search Query (ticket ID, email, machine, session, location, reason, details, coupon)
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      result = result.filter(r =>
        (r.ticketId && r.ticketId.toLowerCase().includes(q)) ||
        (r.id && r.id.toLowerCase().includes(q)) ||
        (r.requestorEmail && r.requestorEmail.toLowerCase().includes(q)) ||
        (r.deviceId && r.deviceId.toLowerCase().includes(q)) ||
        (r.sessionId && r.sessionId.toLowerCase().includes(q)) ||
        (r.storeLocation && r.storeLocation.toLowerCase().includes(q)) ||
        (r.reasonCategory && r.reasonCategory.toLowerCase().includes(q)) ||
        (r.reasonText && r.reasonText.toLowerCase().includes(q)) ||
        (r.couponCode && r.couponCode.toLowerCase().includes(q))
      );
    }

    // Date Filter
    if (this.selectedDateFilter) {
      result = result.filter(r => {
        // Dates are Pacific Time calendar days (PT): a created-at moment is converted to its Pacific date, not its UTC date.
        const reqDate = r.sessionDate || toPacificDateString(r.createdAt) || '';
        return reqDate === this.selectedDateFilter;
      });
    }

    this.filteredRequests = result;
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  // --- KPI Counts ---

  get needsReviewCount(): number {
    return this.allRequests.filter(r => r.status === 'needs_review').length;
  }

  get approvedCount(): number {
    return this.allRequests.filter(r => r.status === 'approved').length;
  }

  get deniedCount(): number {
    return this.allRequests.filter(r => r.status === 'denied').length;
  }

  get totalRequestedAmount(): number {
    return this.allRequests.reduce((sum, r) => sum + (Number(r.originalPaidAmount) || 0), 0);
  }

  // --- Modal Openers ---

  getUserRefundHistory(request: RefundRequest): {
    totalRequests: number;
    approvedCount: number;
    deniedCount: number;
    pendingCount: number;
    totalRefundedAmount: number;
    priorRequests: RefundRequest[];
  } {
    const email = (request.requestorEmail || '').trim().toLowerCase();
    const uid = (request.requestorUid || '').trim();

    const userRequests = this.allRequests.filter(r => {
      const rEmail = (r.requestorEmail || '').trim().toLowerCase();
      const rUid = (r.requestorUid || '').trim();
      return (email && rEmail === email) || (uid && rUid === uid);
    });

    const approved = userRequests.filter(r => r.status === 'approved');
    const denied = userRequests.filter(r => r.status === 'denied');
    const pending = userRequests.filter(r => r.status === 'needs_review' || r.status === 'needs_more_info');
    const totalRefunded = approved.reduce((sum, r) => sum + (Number(r.originalPaidAmount) || 0), 0);

    // Prior requests excluding the current one, sorted newest first
    const prior = userRequests.filter(r => r.id !== request.id);

    return {
      totalRequests: userRequests.length,
      approvedCount: approved.length,
      deniedCount: denied.length,
      pendingCount: pending.length,
      totalRefundedAmount: totalRefunded,
      priorRequests: prior
    };
  }

  openDetailModal(request: RefundRequest): void {
    this.selectedRequest = request;
    this.isDetailModalOpen = true;
    this.closeSessionPanel();
    this.actionSuccessMessage = null;
    this.actionErrorMessage = null;
    this.loadAttachedEvidence(request.id);
  }

  async openSessionPanel(): Promise<void> {
    const req = this.selectedRequest;
    if (!req?.sessionId) return;

    this.isSessionPanelOpen = true;
    this.isSessionLoading = true;
    this.sessionDetail = null;
    this.sessionPanelError = null;

    try {
      if (!req.deviceId || !req.sessionDate) {
        this.sessionPanelError = 'Machine ID or session date is missing on this request, so the session record cannot be looked up.';
        return;
      }
      const sessions = await this.photoboothService.getDeviceSessionsForDate(req.deviceId, req.sessionDate);
      const found = sessions.find(s => s.sessionId === req.sessionId);
      if (!found) {
        this.sessionPanelError = 'No session record was found for this ID on the recorded date.';
        return;
      }

      const pType = (found.payType || found.payMethod || '').toString().toUpperCase();
      const payMethod = found.PaymentMethod
        || (['COIN', 'BILL', 'CASH'].includes(pType) ? 'Cash'
          : pType === 'CARD' ? 'Card'
          : (pType === 'FREE' || pType === 'BYPASS' || found.isSkip) ? 'Bypass'
          : 'Unknown');
      const amountPaid = found.AmountPaid ?? found.TotalPaid ?? found.paid ?? (Number(found.amount) || 0);
      this.sessionDetail = { ...found, PaymentMethod: payMethod, AmountPaid: amountPaid };
    } catch (error) {
      console.error('Error fetching session details:', error);
      this.sessionPanelError = 'Failed to load session details.';
    } finally {
      this.isSessionLoading = false;
    }
  }

  closeSessionPanel(): void {
    this.isSessionPanelOpen = false;
  }

  getSessionPortalUrl(): string {
    const req = this.selectedRequest;
    return `${environment.userPortalUrl}/session/${req?.sessionId}?machine=${req?.deviceId}&date=${req?.sessionDate}`;
  }

  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.closeSessionPanel();
    this.sessionDetail = null;
    this.selectedRequest = null;
    this.evidenceSub?.unsubscribe();
    this.evidenceManifests = [];
    this.evidenceUrls = {};
    this.closeEvidenceLightbox();
  }

  ngOnDestroy(): void {
    this.evidenceSub?.unsubscribe();
    this.brandsSub?.unsubscribe();
    this.devicesSub?.unsubscribe();
    this.storesSub?.unsubscribe();
  }

  loadAttachedEvidence(requestId: string): void {
    this.evidenceSub?.unsubscribe();
    this.evidenceManifests = [];
    this.evidenceUrls = {};

    if (!requestId) return;

    this.isLoadingEvidence = true;
    this.evidenceSub = this.refundService.getRefundEvidence(requestId).subscribe({
      next: (manifests) => {
        this.evidenceManifests = manifests || [];
        this.isLoadingEvidence = false;

        for (const m of this.evidenceManifests) {
          if (!this.evidenceUrls[m.id]) {
            this.refundService.getEvidenceViewUrl(requestId, m.id, 'thumbnail').then(res => {
              if (res?.downloadUrl) {
                this.evidenceUrls[m.id] = res.downloadUrl;
              }
            }).catch(err => {
              console.warn('[RefundManagement] Error loading evidence thumbnail URL:', m.id, err);
            });
          }
        }
      },
      error: (err) => {
        console.warn('[RefundManagement] Error loading evidence manifests:', err);
        this.isLoadingEvidence = false;
      }
    });
  }

  openEvidenceLightbox(manifest: any): void {
    if (!this.selectedRequest?.id || !manifest?.id) return;
    this.activeEvidenceLightboxTitle = manifest.originalFilename || 'Attached Evidence';
    this.activeEvidenceLightboxUrl = this.evidenceUrls[manifest.id] || null;

    this.refundService.getEvidenceViewUrl(this.selectedRequest.id, manifest.id, 'full').then(res => {
      if (res?.downloadUrl) {
        this.activeEvidenceLightboxUrl = res.downloadUrl;
      }
    }).catch(err => {
      console.warn('[RefundManagement] Error fetching full evidence view URL:', err);
    });
  }

  closeEvidenceLightbox(): void {
    this.activeEvidenceLightboxUrl = null;
    this.activeEvidenceLightboxTitle = null;
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }


  openManualCreateModal(): void {
    const todayStr = todayInPacific();
    this.manualEmail = '';
    this.manualStore = '';
    this.manualDeviceId = '';
    this.manualSessionId = '';
    this.manualSessionDate = todayStr;
    this.manualApproxTime = '';
    this.manualPaymentMethod = 'Credit Card';
    this.manualAmount = 10.0;
    this.manualReasonCategory = 'Booth charged but did not start';
    this.manualReasonText = '';
    this.manualDecisionMode = 'instant_approve';
    this.manualCouponTitle = 'Manual Refund Replacement';
    this.manualDiscountType = 'free_session';
    this.manualDiscountValue = 10.0;
    this.manualWantsCash = false;
    this.manualNotes = '';
    this.actionErrorMessage = null;
    this.actionSuccessMessage = null;
    this.defaultManualBrand();
    this.isManualCreateModalOpen = true;
  }

  closeManualCreateModal(): void {
    this.isManualCreateModalOpen = false;
  }

  async confirmManualCreate(): Promise<void> {
    if (!this.manualEmail.trim()) {
      this.actionErrorMessage = 'Please enter a valid customer email address.';
      return;
    }
    if (!this.manualAmount || this.manualAmount <= 0) {
      this.actionErrorMessage = 'Please enter a valid refund amount.';
      return;
    }

    this.isProcessingAction = true;
    this.actionErrorMessage = null;

    try {
      const isAutoApprove = this.manualDecisionMode === 'instant_approve';
      const result = await this.refundService.createManualRefund({
        requestorEmail: this.manualEmail.trim(),
        brandId: this.manualBrandId.trim() || undefined,
        brandName: this.manualBrandName.trim() || undefined,
        deviceId: this.manualDeviceId.trim() || undefined,
        storeLocation: this.manualStore.trim() || this.manualDeviceId.trim(),
        sessionId: this.manualSessionId.trim() || undefined,
        sessionDate: this.manualSessionDate || undefined,
        approximateTime: this.manualApproxTime.trim() || undefined,
        originalPaymentMethod: this.manualPaymentMethod,
        originalPaidAmount: this.manualAmount,
        eligibleCouponAmount: this.manualAmount,
        wantsCashRefund: this.manualWantsCash,
        reasonCategory: this.manualReasonCategory,
        reasonText: this.manualReasonText.trim() || undefined,
        autoApprove: isAutoApprove,
        couponTitle: this.manualCouponTitle.trim() || undefined,
        discountType: this.manualDiscountType,
        discountValue: this.manualDiscountValue,
        notes: this.manualNotes.trim() || undefined
      }, this.staffEmail);

      this.isManualCreateModalOpen = false;
      if (result.couponCode) {
        this.actionSuccessMessage = `Manual refund created and approved! Single-use coupon issued: ${result.couponCode}`;
      } else {
        this.actionSuccessMessage = `Manual refund request added to Needs Review queue (ID: ${result.requestId}).`;
      }
    } catch (err: any) {
      console.error('Error creating manual refund:', err);
      // Brand-resolution failures are actionable (KAN-227): show the exact reason instead of the generic message.
      this.actionErrorMessage = String(err?.message || '').startsWith('Cannot issue the coupon')
        ? err.message
        : 'Failed to create manual refund. Please check inputs and try again.';
    } finally {
      this.isProcessingAction = false;
    }
  }

  openApproveDialog(): void {
    if (!this.selectedRequest) return;
    this.approveCouponTitle = `Refund Replacement (${this.selectedRequest.deviceId || 'PhotoLavie'})`;
    this.approveDiscountType = 'free_session';
    this.approveDiscountValue = this.selectedRequest.eligibleCouponAmount || this.selectedRequest.originalPaidAmount || 10.0;
    this.approveNotes = '';
    this.isApproveModalOpen = true;
  }

  closeApproveDialog(): void {
    this.isApproveModalOpen = false;
  }

  openDenyDialog(): void {
    this.denyRationale = '';
    this.isDenyModalOpen = true;
  }

  closeDenyDialog(): void {
    this.isDenyModalOpen = false;
  }

  openRequestInfoDialog(): void {
    this.requestInfoMessage = '';
    this.isRequestInfoModalOpen = true;
  }

  closeRequestInfoDialog(): void {
    this.isRequestInfoModalOpen = false;
  }

  // --- Decision Actions ---

  async confirmApprove(): Promise<void> {
    if (!this.selectedRequest) return;
    this.isProcessingAction = true;
    this.actionErrorMessage = null;

    try {
      const code = await this.refundService.approveRefund(this.selectedRequest, this.staffEmail, {
        couponTitle: this.approveCouponTitle,
        discountType: this.approveDiscountType,
        discountValue: this.approveDiscountValue,
        notes: this.approveNotes
      });

      this.isApproveModalOpen = false;
      this.actionSuccessMessage = `Refund approved successfully! Single-use coupon code created: ${code} and email notification dispatched.`;
      
      // Update local modal state
      this.selectedRequest.status = 'approved';
      this.selectedRequest.couponCode = code;
      this.selectedRequest.emailDeliveryStatus = 'pending';
    } catch (err: any) {
      console.error('Error approving refund:', err);
      // Brand-resolution failures and validation errors are actionable: show the exact reason.
      const msg = String(err?.message || '');
      this.actionErrorMessage = (msg.startsWith('Cannot issue the coupon') || msg.startsWith('Cannot approve refund'))
        ? err.message
        : 'Failed to approve refund. Please try again.';
    } finally {
      this.isProcessingAction = false;
    }
  }

  async retryEmailDelivery(request?: RefundRequest | null): Promise<void> {
    const target = request || this.selectedRequest;
    if (!target) return;
    this.isProcessingAction = true;
    this.actionErrorMessage = null;
    this.actionSuccessMessage = null;

    try {
      await this.refundService.retryCouponEmail(target, this.staffEmail);
      target.emailDeliveryStatus = 'pending';
      target.emailDeliveryError = null;
      this.actionSuccessMessage = `Email notification re-queued successfully for ${target.requestorEmail}.`;
    } catch (err: any) {
      console.error('Error retrying coupon email:', err);
      this.actionErrorMessage = err?.message || 'Failed to resend coupon email. Please try again.';
    } finally {
      this.isProcessingAction = false;
    }
  }

  async confirmDeny(): Promise<void> {
    if (!this.selectedRequest) return;
    if (!this.denyRationale.trim()) {
      this.actionErrorMessage = 'Please provide a rationale for denying this request.';
      return;
    }

    this.isProcessingAction = true;
    this.actionErrorMessage = null;

    try {
      await this.refundService.denyRefund(this.selectedRequest, this.staffEmail, this.denyRationale.trim());
      this.isDenyModalOpen = false;
      this.actionSuccessMessage = 'Refund request denied.';
      this.selectedRequest.status = 'denied';
      this.selectedRequest.decisionRationale = this.denyRationale.trim();
    } catch (err: any) {
      console.error('Error denying refund:', err);
      this.actionErrorMessage = 'Failed to deny refund. Please try again.';
    } finally {
      this.isProcessingAction = false;
    }
  }

  async confirmRequestInfo(): Promise<void> {
    if (!this.selectedRequest) return;
    if (!this.requestInfoMessage.trim()) {
      this.actionErrorMessage = 'Please specify the information requested from the customer.';
      return;
    }

    this.isProcessingAction = true;
    this.actionErrorMessage = null;

    try {
      await this.refundService.requestMoreInfo(this.selectedRequest, this.staffEmail, this.requestInfoMessage.trim());
      this.isRequestInfoModalOpen = false;
      this.actionSuccessMessage = 'Marked as needing more info.';
      this.selectedRequest.status = 'needs_more_info';
      this.selectedRequest.adminInquiry = this.requestInfoMessage.trim();
    } catch (err: any) {
      console.error('Error requesting info:', err);
      this.actionErrorMessage = 'Failed to update request. Please try again.';
    } finally {
      this.isProcessingAction = false;
    }
  }

  /**
   * Session date/time for the "Session & Machine Details" card. Prefers the exact session start that the validation function
   * verified from the machine's session record (`evidenceSnapshot.timelineBounds.sessionStartTime`, shown in Pacific Time);
   * otherwise falls back to what the customer submitted (a date, plus the approximate time for guided requests).
   */
  getSessionWhen(request: RefundRequest | null | undefined): { text: string; source: 'verified' | 'submitted' | 'unknown' } {
    const verifiedIso = request?.evidenceSnapshot?.timelineBounds?.sessionStartTime;
    if (verifiedIso) {
      const text = formatPacificDateTime(verifiedIso, true);
      if (text !== '-') return { text, source: 'verified' };
    }
    const submittedDate = (request?.sessionDate || '').trim();
    if (submittedDate) {
      const time = request?.approximateTime ? ` (${request.approximateTime} PT)` : '';
      return { text: `${formatPacificDate(submittedDate)}${time}`, source: 'submitted' };
    }
    return { text: 'Not available', source: 'unknown' };
  }

  /** Date (and time for moments) shown in the ERP: always Pacific Time, labelled PT. A `YYYY-MM-DD` string is already a Pacific date. */
  formatDate(timestamp: any): string {
    if (!timestamp) return '-';
    if (typeof timestamp === 'string') {
      return isDateOnlyString(timestamp) ? formatPacificDate(timestamp) : formatPacificDateTime(timestamp);
    }
    return formatPacificDateTime(timestamp);
  }

  /** Date and time with seconds in Pacific Time (PT) for epoch seconds / Firestore Timestamp values. */
  formatPacificTime(value: any): string {
    return formatPacificDateTime(value, true);
  }

  getRiskBandClass(request: RefundRequest): string {
    const band = request.riskBand || (request.riskScore !== undefined ? (request.riskScore >= 70 ? 'high' : request.riskScore >= 50 ? 'needs_review' : request.riskScore >= 25 ? 'elevated' : 'low') : 'low');
    switch (band) {
      case 'high': return 'risk-high';
      case 'needs_review': return 'risk-review';
      case 'elevated': return 'risk-elevated';
      case 'low':
      default:
        return 'risk-low';
    }
  }

  getRiskLabel(request: RefundRequest): string {
    const score = request.riskScore ?? 0;
    const band = request.riskBand || (score >= 70 ? 'high' : score >= 50 ? 'needs_review' : score >= 25 ? 'elevated' : 'low');
    return `${band.toUpperCase()} (${score})`;
  }

  getRiskScoreBreakdown(request: RefundRequest): { factor: string; description: string; points: number }[] {
    const items: { factor: string; description: string; points: number }[] = [];
    const factors = request.riskFactors || [];
    const isGuided = request.identificationMethod === 'guided_match' || !request.sessionId;

    if (factors.includes('guided_match_without_exact_qr') || isGuided) {
      items.push({
        factor: 'Guided Lookup (No QR Scan)',
        description: 'Customer did not scan physical receipt QR; claimed session via manual search.',
        points: 45
      });
    } else {
      items.push({
        factor: 'Base Session Verification',
        description: 'Exact QR code scanned from physical print strip.',
        points: 5
      });
    }

    if (factors.includes('cash_refund_requested') || request.wantsCashRefund) {
      items.push({
        factor: 'Cash Refund Option Selected',
        description: 'Customer requested manual cash/bank refund rather than a replacement voucher.',
        points: 40
      });
    }

    if (factors.includes('duplicate_session_already_refunded')) {
      items.push({
        factor: 'Prior Approved Refund Exists',
        description: 'This exact session ID already has an approved coupon issued previously.',
        points: 80
      });
    } else if (factors.includes('duplicate_session_pending_review')) {
      items.push({
        factor: 'Duplicate Claim In Progress',
        description: 'Another pending claim exists for this same session ID.',
        points: 35
      });
    }

    if (factors.includes('session_exceeds_7_day_window')) {
      items.push({
        factor: 'Exceeds 7-Day Window',
        description: 'Session occurred over 7 calendar days ago, exceeding standard refund eligibility policy.',
        points: 60
      });
    }

    if (factors.includes('session_not_found_on_terminal')) {
      items.push({
        factor: 'Session Missing in Machine Records',
        description: 'Session record was not found in kiosk local telemetry or cloud audit collection.',
        points: 30
      });
    }

    if (factors.includes('unverified_or_zero_payment')) {
      items.push({
        factor: 'Unverified / Non-Revenue Payment',
        description: 'Original session was redeemed with a coupon ($0) or lacked valid payment confirmation.',
        points: 35
      });
    }

    if (factors.includes('evidence_contradicts_claim')) {
      items.push({
        factor: 'Evidence Contradicts Customer Claim',
        description: 'Kiosk telemetry logs or capture verification directly contradict the reported incident.',
        points: 30
      });
    }

    if (factors.includes('high_frequency_requests_30d')) {
      items.push({
        factor: 'High Request Velocity (30 Days)',
        description: 'Customer account submitted 3 or more refund requests within the past 30 days.',
        points: 25
      });
    }

    if (factors.includes('high_coupon_velocity_7d')) {
      items.push({
        factor: 'High Coupon Issuance (7 Days)',
        description: 'Customer account already received 2 or more replacement coupons in the past 7 days.',
        points: 30
      });
    }

    if (factors.includes('photos_unavailable_after_48h')) {
      items.push({
        factor: 'Photos Unavailable After 48h Retention Window',
        description: 'Session occurred over 48 hours ago and unclaimed online photos expired per retention policy.',
        points: 25
      });
    }

    if (factors.includes('preservation_failure_after_claim')) {
      items.push({
        factor: 'Saved Gallery Media Unavailable',
        description: 'Session was saved to requestor gallery but online media is missing; queued for technical review.',
        points: 0
      });
    }

    return items;
  }
}

